import { useState, useRef, useEffect } from "react"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { ScrollArea } from "@/components/ui/scroll-area"
import { GlassCard } from "./GlassCard"
import { AnimatedCard } from "./AnimatedCard"
import { TierBadge } from "./TierBadge"
import { useApexState } from "@/hooks/useApexState"
import { 
  MessageSquare, 
  Send, 
  Crown,
  Star,
  X,
  Phone,
  Video,
  MoreHorizontal,
  Search,
  Pin,
  Smile,
  Paperclip,
  Check,
  CheckCheck
} from "lucide-react"

interface Message {
  id: string
  senderId: string
  senderName: string
  senderAvatar: string
  senderTier?: 'initiate' | 'creator' | 'architect'
  content: string
  timestamp: string
  type: 'text' | 'image' | 'product' | 'system'
  isRead: boolean
  attachments?: Array<{
    type: 'image' | 'product'
    url: string
    metadata?: any
  }>
}

interface Conversation {
  id: string
  type: 'concierge' | 'community' | 'direct'
  title: string
  participants: Array<{
    id: string
    name: string
    avatar: string
    tier: 'initiate' | 'creator' | 'architect'
    isOnline: boolean
  }>
  lastMessage: Message
  unreadCount: number
  isPinned: boolean
  isMuted: boolean
}

interface MessagingSystemProps {
  isOpen: boolean
  onClose: () => void
}

const mockConversations: Conversation[] = [
  {
    id: "concierge",
    type: "concierge",
    title: "Style Concierge",
    participants: [
      {
        id: "concierge-sarah",
        name: "Sarah Kim",
        avatar: "https://images.unsplash.com/photo-1494790108755-2616b67e1c04?w=40&h=40&fit=crop",
        tier: "architect",
        isOnline: true
      }
    ],
    lastMessage: {
      id: "msg-1",
      senderId: "concierge-sarah",
      senderName: "Sarah Kim",
      senderAvatar: "https://images.unsplash.com/photo-1494790108755-2616b67e1c04?w=40&h=40&fit=crop",
      senderTier: "architect",
      content: "I've curated some pieces that would work perfectly with your style preferences. Would you like to see them?",
      timestamp: "2:30 PM",
      type: "text",
      isRead: false
    },
    unreadCount: 2,
    isPinned: true,
    isMuted: false
  },
  {
    id: "community-challenge",
    type: "community",
    title: "Motion & Form Challenge",
    participants: [
      {
        id: "maya",
        name: "Maya Rodriguez",
        avatar: "https://images.unsplash.com/photo-1494790108755-2616b67e1c04?w=40&h=40&fit=crop",
        tier: "architect",
        isOnline: true
      },
      {
        id: "jordan",
        name: "Jordan Kim", 
        avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop",
        tier: "creator",
        isOnline: false
      }
    ],
    lastMessage: {
      id: "msg-2",
      senderId: "jordan",
      senderName: "Jordan Kim",
      senderAvatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop",
      senderTier: "creator",
      content: "Just submitted my entry! The lighting in that shot was perfect 📸",
      timestamp: "1:45 PM",
      type: "text",
      isRead: true
    },
    unreadCount: 0,
    isPinned: false,
    isMuted: false
  },
  {
    id: "direct-alex",
    type: "direct",
    title: "Alex Chen",
    participants: [
      {
        id: "alex",
        name: "Alex Chen",
        avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop",
        tier: "creator",
        isOnline: true
      }
    ],
    lastMessage: {
      id: "msg-3",
      senderId: "alex",
      senderName: "Alex Chen",
      senderAvatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop",
      senderTier: "creator",
      content: "Thanks for the styling tips! The Form Tank works perfectly for my morning routine.",
      timestamp: "11:20 AM",
      type: "text",
      isRead: true
    },
    unreadCount: 0,
    isPinned: false,
    isMuted: false
  }
]

const mockMessages: Record<string, Message[]> = {
  "concierge": [
    {
      id: "msg-c1",
      senderId: "concierge-sarah",
      senderName: "Sarah Kim",
      senderAvatar: "https://images.unsplash.com/photo-1494790108755-2616b67e1c04?w=40&h=40&fit=crop",
      senderTier: "architect",
      content: "Hello! I'm Sarah, your personal style concierge. I'm here to help you discover pieces that perfectly match your aesthetic and lifestyle.",
      timestamp: "Yesterday, 2:15 PM",
      type: "text",
      isRead: true
    },
    {
      id: "msg-c2",
      senderId: "user",
      senderName: "You",
      senderAvatar: "",
      content: "Hi Sarah! I'm looking for pieces that work well for both training and casual wear.",
      timestamp: "Yesterday, 2:18 PM",
      type: "text",
      isRead: true
    },
    {
      id: "msg-c3",
      senderId: "concierge-sarah",
      senderName: "Sarah Kim",
      senderAvatar: "https://images.unsplash.com/photo-1494790108755-2616b67e1c04?w=40&h=40&fit=crop",
      senderTier: "architect",
      content: "Perfect! Based on your movement preferences, I'd recommend pieces from our Kinetic series. They're designed for seamless transitions between active and lifestyle wear.",
      timestamp: "Yesterday, 2:25 PM",
      type: "text",
      isRead: true
    },
    {
      id: "msg-c4",
      senderId: "concierge-sarah",
      senderName: "Sarah Kim",
      senderAvatar: "https://images.unsplash.com/photo-1494790108755-2616b67e1c04?w=40&h=40&fit=crop",
      senderTier: "architect",
      content: "I've curated some pieces that would work perfectly with your style preferences. Would you like to see them?",
      timestamp: "2:30 PM",
      type: "text",
      isRead: false,
      attachments: [
        {
          type: "product",
          url: "https://images.unsplash.com/photo-1556905055-8f358a7a47b2?w=200&h=200&fit=crop",
          metadata: { name: "Apex Kinetic Joggers", price: 180 }
        }
      ]
    }
  ]
}

export function MessagingSystem({ isOpen, onClose }: MessagingSystemProps) {
  const { state } = useApexState()
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [newMessage, setNewMessage] = useState("")
  const [conversations] = useState<Conversation[]>(mockConversations)
  const scrollAreaRef = useRef<HTMLDivElement>(null)

  const filteredConversations = conversations.filter(conv =>
    conv.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.lastMessage.content.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const currentMessages = selectedConversation ? mockMessages[selectedConversation] || [] : []
  const currentConversation = conversations.find(c => c.id === selectedConversation)

  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight
    }
  }, [currentMessages])

  const handleSendMessage = () => {
    if (!newMessage.trim() || !selectedConversation) return
    
    // Add message logic here
    console.log('Sending message:', newMessage)
    setNewMessage("")
  }

  const getTierBadgeStyle = (tier: string) => {
    switch (tier) {
      case 'architect':
        return 'tier-badge-architect text-xs px-2 py-0.5'
      case 'creator':
        return 'tier-badge-creator text-xs px-2 py-0.5'
      default:
        return 'tier-badge-initiate text-xs px-2 py-0.5'
    }
  }

  const getConversationIcon = (type: Conversation['type']) => {
    switch (type) {
      case 'concierge':
        return <Crown size={16} className="text-primary" />
      case 'community':
        return <Star size={16} className="text-accent" />
      default:
        return null
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="w-full max-w-5xl h-[80vh] overflow-hidden">
          <GlassCard className="h-full" blur="lg" opacity="high">
            <div className="flex h-full">
              {/* Conversations Sidebar */}
              <div className="w-80 border-r border-white/10 flex flex-col">
                {/* Header */}
                <div className="p-4 border-b border-white/10">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <MessageSquare size={20} className="text-primary" />
                      <h2 className="text-lg font-semibold text-foreground">Messages</h2>
                    </div>
                    <Button variant="ghost" size="sm" onClick={onClose}>
                      <X size={20} />
                    </Button>
                  </div>
                  
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Search conversations..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 bg-input/50 border-white/20"
                    />
                  </div>
                </div>

                {/* Conversations List */}
                <ScrollArea className="flex-1">
                  <div className="p-2">
                    {filteredConversations.map(conversation => (
                      <AnimatedCard key={conversation.id}>
                        <button
                          onClick={() => setSelectedConversation(conversation.id)}
                          className={`
                            w-full p-3 rounded-lg text-left transition-colors mb-2
                            ${selectedConversation === conversation.id
                              ? 'bg-primary/10 border border-primary/20'
                              : 'hover:bg-white/5'
                            }
                          `}
                        >
                          <div className="flex items-start gap-3">
                            <div className="relative">
                              {conversation.participants.length === 1 ? (
                                <Avatar className="w-12 h-12">
                                  <AvatarImage src={conversation.participants[0].avatar} />
                                  <AvatarFallback>{conversation.participants[0].name.slice(0, 2)}</AvatarFallback>
                                </Avatar>
                              ) : (
                                <div className="w-12 h-12 bg-muted/20 rounded-full flex items-center justify-center">
                                  <MessageSquare size={20} className="text-muted-foreground" />
                                </div>
                              )}
                              
                              {conversation.participants[0]?.isOnline && (
                                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-background" />
                              )}
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  {getConversationIcon(conversation.type)}
                                  <h4 className="font-medium text-foreground truncate">
                                    {conversation.title}
                                  </h4>
                                  {conversation.isPinned && (
                                    <Pin size={12} className="text-accent" />
                                  )}
                                </div>
                                <div className="flex items-center gap-1">
                                  <span className="text-xs text-muted-foreground">
                                    {conversation.lastMessage.timestamp}
                                  </span>
                                  {conversation.unreadCount > 0 && (
                                    <Badge className="bg-primary text-black text-xs px-1.5 py-0.5">
                                      {conversation.unreadCount}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              
                              <p className="text-sm text-muted-foreground truncate">
                                {conversation.lastMessage.senderName !== 'You' && `${conversation.lastMessage.senderName}: `}
                                {conversation.lastMessage.content}
                              </p>

                              {conversation.participants[0]?.tier && (
                                <TierBadge tier={conversation.participants[0].tier} size="xs" className="mt-1" />
                              )}
                            </div>
                          </div>
                        </button>
                      </AnimatedCard>
                    ))}
                  </div>
                </ScrollArea>
              </div>

              {/* Chat Area */}
              <div className="flex-1 flex flex-col">
                {selectedConversation && currentConversation ? (
                  <>
                    {/* Chat Header */}
                    <div className="p-4 border-b border-white/10">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={currentConversation.participants[0]?.avatar} />
                            <AvatarFallback>{currentConversation.title.slice(0, 2)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <h3 className="font-semibold text-foreground flex items-center gap-2">
                              {getConversationIcon(currentConversation.type)}
                              {currentConversation.title}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {currentConversation.participants[0]?.isOnline ? 'Online now' : 'Last seen recently'}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {currentConversation.type === 'concierge' && (
                            <>
                              <Button variant="ghost" size="sm">
                                <Phone size={16} />
                              </Button>
                              <Button variant="ghost" size="sm">
                                <Video size={16} />
                              </Button>
                            </>
                          )}
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal size={16} />
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Messages */}
                    <ScrollArea ref={scrollAreaRef} className="flex-1 p-4">
                      <div className="space-y-4">
                        {currentMessages.map(message => (
                          <div
                            key={message.id}
                            className={`flex ${message.senderId === 'user' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`max-w-xs lg:max-w-md ${message.senderId === 'user' ? 'order-2' : 'order-1'}`}>
                              {message.senderId !== 'user' && (
                                <div className="flex items-center gap-2 mb-1">
                                  <Avatar className="w-6 h-6">
                                    <AvatarImage src={message.senderAvatar} />
                                    <AvatarFallback className="text-xs">{message.senderName.slice(0, 2)}</AvatarFallback>
                                  </Avatar>
                                  <span className="text-xs text-muted-foreground">{message.senderName}</span>
                                  {message.senderTier && (
                                    <Badge className={getTierBadgeStyle(message.senderTier)}>
                                      {message.senderTier}
                                    </Badge>
                                  )}
                                </div>
                              )}
                              
                              <div className={`
                                rounded-2xl px-4 py-2 ${
                                  message.senderId === 'user'
                                    ? 'bg-primary text-black'
                                    : 'bg-muted/20 text-foreground'
                                }
                              `}>
                                <p className="text-sm">{message.content}</p>
                                
                                {message.attachments && (
                                  <div className="mt-2 space-y-2">
                                    {message.attachments.map((attachment, index) => (
                                      <div key={index} className="bg-white/10 rounded-lg p-2">
                                        {attachment.type === 'product' && (
                                          <div className="flex items-center gap-2">
                                            <img 
                                              src={attachment.url} 
                                              alt="Product" 
                                              className="w-12 h-12 rounded object-cover"
                                            />
                                            <div>
                                              <p className="text-xs font-medium">{attachment.metadata?.name}</p>
                                              <p className="text-xs opacity-70">${attachment.metadata?.price}</p>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              
                              <div className={`flex items-center gap-1 mt-1 ${message.senderId === 'user' ? 'justify-end' : 'justify-start'}`}>
                                <span className="text-xs text-muted-foreground">{message.timestamp}</span>
                                {message.senderId === 'user' && (
                                  message.isRead ? <CheckCheck size={12} className="text-primary" /> : <Check size={12} className="text-muted-foreground" />
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>

                    {/* Message Input */}
                    <div className="p-4 border-t border-white/10">
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm">
                          <Paperclip size={16} />
                        </Button>
                        
                        <div className="flex-1 relative">
                          <Input
                            placeholder="Type a message..."
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                            className="pr-12 bg-input/50 border-white/20"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            className="absolute right-1 top-1/2 transform -translate-y-1/2"
                          >
                            <Smile size={16} />
                          </Button>
                        </div>
                        
                        <Button
                          onClick={handleSendMessage}
                          disabled={!newMessage.trim()}
                          className="bg-primary hover:bg-primary/90 text-black"
                        >
                          <Send size={16} />
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-center">
                    <div>
                      <MessageSquare size={64} className="mx-auto text-muted-foreground opacity-50 mb-4" />
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        Select a conversation
                      </h3>
                      <p className="text-muted-foreground">
                        Choose a conversation from the sidebar to start messaging
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </GlassCard>
        </div>
      </div>
    </div>
  )
}